import '@fontsource-variable/lexend';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider as JotaiProvider } from 'jotai';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { createQueryClient, setQueryToast } from '@/api/queryClient';
import { App } from '@/App';
import { bootstrapAuth, type AuthSession } from '@/auth/bootstrap';
import { AppThemeProvider } from '@/components/layout/AppThemeProvider';
import { ToastHost } from '@/components/ui';
import { useToast } from '@/hooks';

/**
 * Bootstrap (spec §9.2): MSAL → configureApiClient → render → `/access/me`.
 *
 * Authentication is resolved *before* React mounts, so no component can fire a request without a
 * token provider in place — the alternative is a first render that 401s and a flash of an empty
 * shell.
 */
async function start(): Promise<void> {
  const session = await bootstrapAuth();
  const queryClient = createQueryClient();
  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <JotaiProvider>
        <QueryClientProvider client={queryClient}>
          <AppThemeProvider>
            <CssBaseline />
            <BrowserRouter>
              <Root session={session} />
            </BrowserRouter>
            <ToastHost />
          </AppThemeProvider>
        </QueryClientProvider>
      </JotaiProvider>
    </StrictMode>,
  );
}

function Root({ session }: { session: AuthSession }) {
  const toast = useToast();

  // Query and mutation failures report through the same toast host as everything else (§9.4).
  useEffect(() => {
    setQueryToast((message, severity) =>
      severity === 'error' ? toast.error(message) : toast.warning(message),
    );
    return () => setQueryToast(null);
  }, [toast]);

  return (
    <App
      account={session.account}
      onSignOut={() => {
        void session.signOut();
      }}
    />
  );
}

void start();
